import { useEffect, useState, useCallback } from 'react'
import {
  CART_CHANGED_EVENT,
  CART_STORAGE_KEY,
  EMPTY_CART,
  type Cart,
  type CartItem,
} from './types'
import { readCart, writeCart, cartCount, clearCart as clearStorage } from './storage'

/**
 * Single source of truth for cart state. Subscribes to both:
 *   - `storage` events (cross-tab)
 *   - `al:cart:changed` events (same-tab, dispatched by writeCart)
 *
 * The cart icon widget and the spare-parts SPA both consume this hook so
 * they stay in sync regardless of which mount triggered the change.
 */
export function useCart(): {
  cart: Cart
  count: number
  addItem: (item: CartItem) => void
  removeItem: (sku: string) => void
  setQty: (sku: string, qty: number) => void
  clear: () => void
} {
  const [cart, setCart] = useState<Cart>(() => (typeof window === 'undefined' ? EMPTY_CART : readCart()))

  useEffect(() => {
    const refresh = () => setCart(readCart())
    const onStorage = (e: StorageEvent) => {
      if (e.key === CART_STORAGE_KEY) refresh()
    }
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<Cart>).detail
      if (detail) setCart(detail)
      else refresh()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(CART_CHANGED_EVENT, onChanged)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(CART_CHANGED_EVENT, onChanged)
    }
  }, [])

  const addItem = useCallback((item: CartItem) => {
    const current = readCart()
    const existing = current.items.find((i) => i.sku === item.sku)
    const items = existing
      ? current.items.map((i) => (i.sku === item.sku ? { ...i, qty: i.qty + item.qty } : i))
      : [...current.items, item]
    writeCart({ ...current, items })
  }, [])

  const removeItem = useCallback((sku: string) => {
    const current = readCart()
    writeCart({ ...current, items: current.items.filter((i) => i.sku !== sku) })
  }, [])

  const setQty = useCallback((sku: string, qty: number) => {
    const current = readCart()
    if (qty <= 0) {
      writeCart({ ...current, items: current.items.filter((i) => i.sku !== sku) })
      return
    }
    writeCart({
      ...current,
      items: current.items.map((i) => (i.sku === sku ? { ...i, qty } : i)),
    })
  }, [])

  const clear = useCallback(() => {
    clearStorage()
  }, [])

  return { cart, count: cartCount(cart), addItem, removeItem, setQty, clear }
}
